import { createSelector } from "reselect";

import {
  calculateBuyerBalanceSummaryDataFromLots,
  calculatePaymentsTotalCentsFromBusiness,
  getClearingSaleLotTitle,
  type BuyerPaymentSummaryData,
} from "lib/clearingSaleInvoice";
import { EMPTY_OBJECT } from "lib";

import { getPayments, getSaleLots, getBusinesses } from "./root";
import { selectSaleLotIdsByBuyerIdLookup } from "./indexes";
import { selectDeploymentSaleBySaleLotIdLookup } from "./sales";
import {
  selectActiveLivestockAgentDeploymentMasterBusinessId,
  selectShouldChargeGSTByBusinessIdLookup,
} from "./businesses";

export type {
  BuyerPaymentSummaryTotals,
  BuyerPaymentSummaryItem,
  BuyerPaymentSummaryData,
} from "lib/clearingSaleInvoice";

/**
 * Calculate buyer payment summary data
 */
export const selectBuyerBalanceSummaryByBuyerId = createSelector(
  [
    getSaleLots,
    getBusinesses,
    selectActiveLivestockAgentDeploymentMasterBusinessId,
    selectShouldChargeGSTByBusinessIdLookup,
    selectDeploymentSaleBySaleLotIdLookup,
    selectSaleLotIdsByBuyerIdLookup,
    getPayments,
  ],
  (
    saleLots,
    businesses,
    deploymentMasterBusinessId,
    shouldChargeGSTByBusinessIdLookup,
    deploymentSaleBySaleLotIdLookup,
    saleLotIdsByBuyerIdLookup,
    payments,
  ) => {
    const deploymentMasterBusiness =
      deploymentMasterBusinessId != null
        ? (businesses as Record<string, Business | undefined>)[
            deploymentMasterBusinessId
          ]
        : undefined;
    const chargeBuyersPremiumGst =
      deploymentMasterBusiness?.isGSTRegistered === true;

    return (
      Object.entries(saleLotIdsByBuyerIdLookup).reduce<
        Record<string, BuyerPaymentSummaryData>
      >((acc, [buyerId, saleLotIds]) => {
        const buyerSaleLots = (saleLotIds as string[]).map(
          (id: string) => saleLots[id],
        );

        const lots = buyerSaleLots.map(saleLot => {
          const chargeSalePriceGst = saleLot.isGSTExempt
            ? false
            : shouldChargeGSTByBusinessIdLookup[saleLot.vendor_id] === true;

          return {
            saleLot,
            lotTitle: getClearingSaleLotTitle(saleLot),
            chargeSalePriceGst,
          };
        });

        const paymentsTotalCents = calculatePaymentsTotalCentsFromBusiness(
          payments,
          buyerId,
        );

        acc[buyerId] = calculateBuyerBalanceSummaryDataFromLots(
          lots,
          deploymentSaleBySaleLotIdLookup,
          chargeBuyersPremiumGst,
          paymentsTotalCents,
        );

        return acc;
      }, {}) || EMPTY_OBJECT
    );
  },
);
